import * as React from 'react';
import {useEffect, useRef, useState} from 'react';
import {Asset} from "../../api";
import {Document, Page} from "react-pdf";
import {URL_HELPER} from "../../App";
import {Modal} from "@coreui/coreui/dist/js/coreui";
import AssetDetails from "./AssetDetails";
import iconClose from "../../assets/icons/close.svg";
import iconZoomIn from "../../assets/icons/zoom-in.svg";
import iconZoomOut from "../../assets/icons/zoom-out.svg";
import {IAPIComponent} from "../pages/AssetsPage";
import {cartButtonText, CartItem, itemAlreadyAddedToCart, SetCart, toggleCart} from '../utils/CartHelper';

interface AssetModalProps {
    api: IAPIComponent["api"];
    cart: Array<CartItem>;
    setCart: SetCart;
}


export const AssetModal: React.FC<AssetModalProps> = ({api, cart, setCart}) => {
    const [open, setOpen] = useState<boolean>(false);
    const [pdfWidth, setPDFWidth] = useState<number>(500);
    const [asset, setAsset] = useState<Asset | null>(null);
    const [zoomed, setZoomed] = useState<boolean>(false);

    const popupEl = useRef<HTMLDivElement>(null);
    const previewColumn = useRef<HTMLDivElement>(null);
    const pdfPage = useRef<Page>(null);
    const video = useRef<HTMLVideoElement>(null);

    const DESCRIPTION_FIELDNAME = "customFields_text_342_en";

    useEffect(() => {
        localStorage.setItem("cart_" + URL_HELPER.getMediaCenterId(), JSON.stringify(cart));
    }, [cart]);
    
    /**
     * Request and render Asset Details, then open the popup
     * @param event contains assetId to display
     */
    function openPopup(event: Event) {
        let assetId = (event as CustomEvent).detail.assetId;
        if (api) {
            api.getAsset({
                mediaCenterId: URL_HELPER.getMediaCenterId(),
                assetId: assetId
            }).then(response => {
                setAsset(response);
                setOpen(true);
            });
        }
    }

    function closePopup() {
        setOpen(false);
        setZoomed(false);
    }

    function getDetailsFields() {
        return asset?.fields?.filter(f => f.name !== DESCRIPTION_FIELDNAME);
    }

    function getDescriptionField() {
        return asset?.fields?.filter(f => f.name === DESCRIPTION_FIELDNAME);
    }

    function updatePDFWidth() {
        if (previewColumn.current && pdfPage.current) {
            let padding = window.getComputedStyle(previewColumn.current, null).getPropertyValue("padding-right");
            setPDFWidth(previewColumn.current.clientWidth - Number.parseInt(padding));
        }
    }

    function renderZoomable(content: JSX.Element) {
        return <div className={"asset-modal-preview" + (zoomed ? " zoomed" : "")} onClick={() => setZoomed(!zoomed)}>
            {content}
            <div className="asset-modal-preview-icon">
                <img src={zoomed ? iconZoomOut : iconZoomIn} alt=""/>
            </div>
        </div>;
    }

    function renderPdf(assetBody: Asset) {
        return renderZoomable(<Document file={URL_HELPER.getDownloadUrl() + "/" + assetBody.id + "/" + assetBody.name}
                                        onLoadSuccess={updatePDFWidth}>
            <Page pageNumber={1} ref={pdfPage} width={pdfWidth}/>
        </Document>);
    }

    function renderVideo(assetBody: Asset) {
        return <video className="w-100" controls ref={video}>
            <source
                src={URL_HELPER.getDownloadUrl() + "/" + assetBody.id + "/" + assetBody.name}
                type={"video/" + assetBody.extension}/>
        </video>;
    }

    function renderImage(assetBody: Asset) {
        const previewUrl = URL_HELPER.getThumbnailUrl() + "/" + assetBody.id;
        return renderZoomable(<img className="w-100" src={previewUrl} alt={assetBody.name}/>);
    }

    function renderPreview(assetBody: Asset) {
        switch (assetBody.extension) {
            case "pdf":
                return renderPdf(assetBody);
            case "mp4":
                return renderVideo(assetBody);
            default:
                return renderImage(assetBody);
        }

    }

    function renderPopup() {
        if (asset == null) {
            return "";
        }

        let id = asset.fields?.filter(field => field.name === "Id");

        return (
            <div className={"modal-dialog modal-" + (zoomed ? "xl" : "lg")} role="document">
                <div className="modal-content">
                    <div className="modal-header">
                        <div>
                            <h3 title={asset.name}>
                                {asset.name}
                            </h3>
                        </div>
                        <img className="icon" src={iconClose} aria-label="Close" data-dismiss="modal" role="button"
                             onClick={closePopup} alt="Clear" title="Clear"/>
                    </div>
                    <div className="modal-body">
                        <div className="row">
                            <div className={"col-sm-" + (zoomed ? "12" : "6")} ref={previewColumn}>
                                {renderPreview(asset)}
                                <AssetDetails fields={getDescriptionField()}/>
                                {id && id.length ? <small>&nbsp;(ID: {id[0].value.toString()})</small> : <></>}
                            </div>
                            <div className={"mt-md-6 col-sm-" + (zoomed ? "12" : "6")}>
                                <AssetDetails fields={getDetailsFields()}/>
                            </div>
                        </div>
                    </div>
                    <div className="modal-footer">
                        <a href={URL_HELPER.getDownloadUrl() + '/' + asset.id + '/' + asset.name} title="Download original format">
                            <button type="button" className="btn btn-primary">Download Original</button>
                        </a>
                        {['jpg', 'jpeg', 'png', 'tif'].includes(asset.extension) && 
                            <a href={URL_HELPER.getDefaultDownloadUrl(asset)} title="Download default format">
                                <button type="button" className="btn btn-primary">Download Optimized</button>
                            </a>
                        }
                        <button type="button" className={`btn ${itemAlreadyAddedToCart(asset, cart) ? 'btn-secondary' : 'btn-primary'}`} title={cartButtonText(asset, cart)} onClick={() => {toggleCart(asset, cart, setCart)}}>{cartButtonText(asset, cart)}</button>
                    </div>
                </div>
            </div>
        );
    }

    function onHidden() {
        if (video.current) {
            video.current.pause();
        }
    }

    useEffect(() => {
        document.addEventListener("open-popup", openPopup);
        document.addEventListener("resize", updatePDFWidth);
        if (popupEl.current) {
            popupEl.current.addEventListener("hide.coreui.modal", onHidden);
        }
        return () => {
            window.removeEventListener('open-popup', openPopup);
            document.removeEventListener("resize", updatePDFWidth);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (popupEl.current) {
            let modal = new Modal(popupEl.current);
            if (open) {
                modal.show();
            } else {
                modal.hide();
            }
        }
    }, [asset, open]);

    useEffect(updatePDFWidth, [zoomed]);

    return (
        <div className="asset-modal modal fade" role="dialog" ref={popupEl}>
            {renderPopup()}
        </div>
    );
};

export default AssetModal;
